import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, FlaskConical } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { ErrorEventsTab } from './ErrorEventsTab';
import { DeadLetterQueueTab } from './DeadLetterQueueTab';
import { SeverityConfigTab } from './SeverityConfigTab';
import { ErrorEventTestDialog } from './ErrorEventTestDialog';
import { apiService, CreateTestErrorEventRequest, DlqMessage } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';

/**
 * Admin-Seite „Error Events" — mandantenübergreifende Betriebsübersicht über
 * Fehler-Events aller Services samt Dead Letter Queue.
 *
 * Die DLQ-Liste wird hier (statt in {@link DeadLetterQueueTab} selbst)
 * geladen: der Zähler im Tab-Trigger-Badge muss auch sichtbar sein, während
 * der Tab-Inhalt selbst nicht gemountet ist (Radix `TabsContent` rendert
 * inaktive Tabs nicht).
 */
export function ErrorEventsPage() {
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testSubmitting, setTestSubmitting] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Tab URL-gesteuert (`?tab=dlq`, `?tab=severity`), damit die Kopfzeilen-Glocke
  // direkt in den DLQ-Tab verlinken und ein Severity-Link geteilt werden kann.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'dlq' || tabParam === 'severity' ? tabParam : 'events';
  const handleTabChange = (value: string) => {
    setSearchParams(value === 'events' ? {} : { tab: value }, { replace: true });
  };

  const [dlqMessages, setDlqMessages] = useState<DlqMessage[]>([]);
  const [dlqTotal, setDlqTotal] = useState(0);
  const [dlqLoading, setDlqLoading] = useState(true);
  const [dlqError, setDlqError] = useState<string | null>(null);

  const loadDlq = useCallback(async () => {
    try {
      setDlqLoading(true);
      setDlqError(null);
      const response = await apiService.getDlqMessages();
      setDlqMessages(response.messages);
      setDlqTotal(response.total);
    } catch (err) {
      setDlqError('DLQ-Nachrichten konnten nicht geladen werden');
      toast.error(extractErrorMessage(err, 'DLQ-Nachrichten konnten nicht geladen werden'));
    } finally {
      setDlqLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDlq();
  }, [loadDlq]);

  const handleCreateTestEvent = async (payload: CreateTestErrorEventRequest) => {
    try {
      setTestSubmitting(true);
      await apiService.createTestErrorEvent(payload);
      toast.success('Test-Event ausgelöst');
      setRefreshSignal((s) => s + 1);
      await loadDlq();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Test-Event konnte nicht ausgelöst werden'));
    } finally {
      setTestSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <AlertTriangle className="h-6 w-6" />
            Error Events
          </h1>
          <p className="text-gray-600">Fehler-Events aller Services — mandantenübergreifende Betriebsübersicht</p>
        </div>
        <Button variant="outline" onClick={() => setTestDialogOpen(true)}>
          <FlaskConical className="mr-2 h-4 w-4" />
          Test-Event erzeugen
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="events">Error Events</TabsTrigger>
          <TabsTrigger value="dlq" className="gap-2">
            Dead Letter Queue
            {dlqTotal > 0 && <Badge className="bg-red-600 text-white">{dlqTotal}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="severity">Severity</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <ErrorEventsTab refreshSignal={refreshSignal} />
        </TabsContent>

        <TabsContent value="dlq">
          <DeadLetterQueueTab
            messages={dlqMessages}
            total={dlqTotal}
            loading={dlqLoading}
            error={dlqError}
            onReload={loadDlq}
          />
        </TabsContent>

        <TabsContent value="severity">
          <SeverityConfigTab />
        </TabsContent>
      </Tabs>

      <ErrorEventTestDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        onSubmit={handleCreateTestEvent}
        submitting={testSubmitting}
      />
    </div>
  );
}
